import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Stack, Text, Group, Badge, Loader, Alert } from '@mantine/core';
import { AlertCircle } from 'lucide-react';
import { getConfigurationTemplateSafe } from '@services/configurationTemplateManager';
import { parseJsonSafe, ensureArray } from '@utils/parsing';

interface SessionConfigurationViewProps {
  sessionId: number;
}

interface Step {
  id: string;
  type: string;
  question?: string;
  answers?: Array<{ text?: string; label?: string; [key: string]: unknown }>;
  videoId?: string;
  audioId?: string;
  scenarioId?: string;
  condition?: string;
  [key: string]: unknown;
}

export const SessionConfigurationView: React.FC<
  SessionConfigurationViewProps
> = ({ sessionId }) => {
  const {
    data: template,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['session-configuration', sessionId],
    queryFn: async () => {
      const result = await getConfigurationTemplateSafe(sessionId);
      return result;
    },
    enabled: Boolean(sessionId),
  });

  const steps = useMemo(() => {
    if (!template) return [];

    const configCandidate = parseJsonSafe<Record<string, unknown>>(
      (template as unknown as { config?: string | Record<string, unknown> })
        .config,
      'Errore parsing config string'
    );

    const stepsSource =
      configCandidate?.steps ??
      parseJsonSafe<unknown>(template.steps, 'Errore parsing steps string') ??
      template.steps;

    return ensureArray<Step>(stepsSource);
  }, [template]);

  if (isLoading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <Loader size='md' />
        <Text mt='md' size='sm' c='dimmed'>
          Caricamento configurazione...
        </Text>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        icon={<AlertCircle size={16} />}
        title='Errore'
        color='red'
        variant='light'
      >
        Errore nel caricamento della configurazione
      </Alert>
    );
  }

  if (!template || steps.length === 0) {
    return (
      <Alert
        icon={<AlertCircle size={16} />}
        title='Nessuna configurazione'
        color='yellow'
        variant='light'
      >
        Nessuna configurazione disponibile per questa sessione
      </Alert>
    );
  }

  return (
    <Stack gap='md' p='md'>
      <Text fw={500} mt='md'>
        Steps della configurazione ({steps.length}):
      </Text>

      <Stack gap='sm'>
        {steps.map((step, index) => (
          <Card key={step.id || index} withBorder p='md' radius='sm'>
            <Group justify='space-between' mb='xs'>
              <Text fw={500}>Step {index + 1}</Text>
              <Badge variant='light' color='blue'>
                {step.type || 'N/A'}
              </Badge>
            </Group>
            <Stack gap='xs' mt='xs'>
              {step.type === 'question' && (
                <>
                  {step.question && (
                    <Text size='sm'>
                      <strong>Domanda:</strong> {String(step.question)}
                    </Text>
                  )}
                  {step.answers && Array.isArray(step.answers) && (
                    <div>
                      <Text size='sm' fw={500} mb='xs'>
                        Risposte:
                      </Text>
                      {step.answers.map((answer, idx) => (
                        <Text key={idx} size='xs' c='dimmed' ml='md'>
                          • {answer.text || answer.label || String(answer)}
                        </Text>
                      ))}
                    </div>
                  )}
                </>
              )}
              {step.type === 'video' && (
                <>
                  {step.videoId && (
                    <Text size='sm'>
                      <strong>Video ID:</strong> {String(step.videoId)}
                    </Text>
                  )}
                  {step.audioId && (
                    <Text size='sm'>
                      <strong>Audio ID:</strong> {String(step.audioId)}
                    </Text>
                  )}
                </>
              )}
              {step.type === 'scenario' && (
                <>
                  {step.scenarioId && (
                    <Text size='sm'>
                      <strong>Scenario ID:</strong> {String(step.scenarioId)}
                    </Text>
                  )}
                </>
              )}
              {step.condition && (
                <Text size='xs' c='dimmed' mt='xs'>
                  <strong>Condizione:</strong> {String(step.condition)}
                </Text>
              )}
            </Stack>
          </Card>
        ))}
      </Stack>
    </Stack>
  );
};
